import Decimal from 'decimal.js';
import { useMemo, useState } from 'react';

import type { AmortizationMethod, CreatePrepaidContract, SchedulePeriod } from '@dcc-erp/shared';
import { buildAmortizationSchedule, fmt } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { BranchSelect, DepartmentSelect } from '../dimensions';
import { PREPAID_ASSET_CODE, PP_CATS, getCat, type CatCode, type PPItem } from './pp-helpers';
import { useCoa, useExpenseDirect, usePrepaidSettings } from '../../lib/queries';

// P8 — renew/clone prefill carried from an expiring contract.
interface ClonePrefill {
  cat: CatCode;
  total: number;
  period: number;
  description: string;
  start: string; // a renewal starts the day after the source contract ends
}

export interface AddModalProps {
  companyId: string | undefined;
  onClose: () => void;
  onSubmit: (data: CreatePrepaidContract) => void;
  submitting: boolean;
  prefill?: ClonePrefill;
  existing?: readonly PPItem[];
}

export function PPAddModal({
  companyId,
  onClose,
  onSubmit,
  submitting,
  prefill,
  existing,
}: AddModalProps) {
  const [cat, setCat] = useState<CatCode>(prefill?.cat ?? 'INS');
  const [total, setTotal] = useState<number>(prefill?.total ?? 1_200_000);
  const [period, setPeriod] = useState<number>(prefill?.period ?? 12);
  const [start, setStart] = useState<string>(prefill?.start ?? '2026-06-01');
  const [description, setDescription] = useState<string>(prefill?.description ?? '');
  // P9 §9.7 — what-if: the chosen straight-line method (also threaded into the
  // create payload so the posted schedule matches the preview the user saw).
  const [method, setMethod] = useState<AmortizationMethod>('EVEN');

  // P9 §9.3 — duplicate guard: warn (non-blocking) when another contract already
  // has the same description, or the same total starting on the same day.
  const dup = useMemo(() => {
    const d = description.trim().toLowerCase();
    if (!d) return undefined;
    return (existing ?? []).find(
      (i) =>
        i.desc.trim().toLowerCase() === d ||
        (i.total === total && i.start === start && i.apiStatus !== 'TERMINATED'),
    );
  }, [existing, description, total, start]);
  const [branchId, setBranchId] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [expenseAccountCode, setExpenseAccountCode] = useState<string>('');

  // Postable expense accounts come from the real COA (control/header accounts
  // can't be posted). The hardcoded PP_CATS array now only drives the category
  // visual (tone/label) on the register/summary tabs — the GL code posted is
  // whatever the user picks here.
  const { data: coa = [] } = useCoa(companyId, true);
  const expenseAccounts = useMemo(
    () => coa.filter((a) => (a.type === 'EXPENSE' || a.type === 'COST') && !a.isControl),
    [coa],
  );

  // Materiality (P6): below the company threshold a prepaid is immaterial → offer
  // to expense it directly (Dr expense / Cr cash, no schedule).
  const { data: prepaidSettings } = usePrepaidSettings(companyId);
  const expenseDirectMut = useExpenseDirect(companyId);
  const threshold =
    prepaidSettings?.prepaidMaterialityThreshold != null
      ? Number(prepaidSettings.prepaidMaterialityThreshold)
      : null;
  const belowThreshold = threshold !== null && total > 0 && total < threshold;
  const [expenseDirectErr, setExpenseDirectErr] = useState<string | null>(null);

  const handleExpenseDirect = (): void => {
    if (!expenseAccountCode) return;
    setExpenseDirectErr(null);
    expenseDirectMut.mutate(
      {
        data: {
          description: description || getCat(cat).label,
          date: start,
          totalAmount: total,
          expenseAccountCode,
          // cashAccountCode omitted → service resolves the per-company BANK_PRIMARY.
          branchId: branchId || null,
          departmentId: departmentId || null,
        },
      },
      {
        onSuccess: () => onClose(),
        onError: (e) =>
          setExpenseDirectErr(e instanceof Error ? e.message : 'ลงค่าใช้จ่ายไม่สำเร็จ'),
      },
    );
  };

  const handleSubmit = (): void => {
    if (!expenseAccountCode) return;
    // Build endDate in UTC (last day of start-month + period − 1), mirroring the
    // what-if `endUTC` exactly so the persisted endDate matches the previewed
    // schedule (a local Date here drifts the day by one in UTC+ timezones).
    const sy = Number(start.slice(0, 4));
    const sm = Number(start.slice(5, 7));
    const endD = new Date(Date.UTC(sy, sm - 1 + period, 0));
    onSubmit({
      description: description || `${getCat(cat).label} — ${period} เดือน`,
      vendorId: null,
      startDate: start,
      endDate: endD.toISOString().slice(0, 10),
      totalAmount: total,
      expenseAccountCode,
      branchId: branchId || null,
      departmentId: departmentId || null,
      amortizationMethod: method,
      // cashAccountCode omitted → service resolves the per-company BANK_PRIMARY.
    });
  };

  // Actual last amortization day (UTC last-day of start-month + period − 1) —
  // matches the persisted endDate + the what-if final period (no day-28 approx).
  const endDate = (() => {
    const sy = Number(start.slice(0, 4));
    const sm = Number(start.slice(5, 7));
    return new Date(Date.UTC(sy, sm - 1 + period, 0));
  })();

  // P9 §9.7 — what-if simulator. Compute the FULL schedule for BOTH methods via
  // the shared @dcc-erp/shared engine (the exact same builder the backend posts
  // with), so the side-by-side preview recomputes live on amount/term/start edits
  // and never drifts from reality. Dates are built in UTC to match the engine.
  const whatIf = useMemo<{
    even: SchedulePeriod[];
    prorate: SchedulePeriod[];
    monthly: number;
  }>(() => {
    const [sy, sm] = start.split('-').map(Number);
    if (!sy || !sm || !(total > 0) || period < 1) {
      return { even: [], prorate: [], monthly: 0 };
    }
    const startUTC = new Date(Date.UTC(sy, sm - 1, Number(start.split('-')[2]) || 1));
    const endUTC = new Date(Date.UTC(sy, sm - 1 + period, 0)); // last day of (start month + period − 1)
    const amount = new Decimal(total);
    const even = buildAmortizationSchedule(amount, startUTC, endUTC, 'EVEN');
    const prorate = buildAmortizationSchedule(amount, startUTC, endUTC, 'PRORATE_FIRST_LAST');
    return { even, prorate, monthly: even[0] ? even[0].amount.toNumber() : 0 };
  }, [total, period, start]);
  const monthly = whatIf.monthly;
  // Rows for the selected method drive the per-period header; the comparison
  // table pairs EVEN vs PRORATE side by side.
  const previewSched = method === 'EVEN' ? whatIf.even : whatIf.prorate;
  const thMonth = (p: SchedulePeriod): string => {
    const y = Number(p.period.slice(0, 4));
    const m0 = Number(p.period.slice(4, 6)) - 1;
    return new Date(Date.UTC(y, m0, 1)).toLocaleDateString('th-TH', {
      month: 'short',
      year: '2-digit',
      timeZone: 'UTC',
    });
  };

  // Preview debit account reflects the COA account the user picked; before a
  // selection is made we fall back to the category's display label.
  const selectedAcct = expenseAccounts.find((a) => a.code === expenseAccountCode);
  const catObj = getCat(cat);
  const acctCode = selectedAcct?.code ?? '';
  const acctName = selectedAcct?.nameTh ?? catObj.label;
  // Tint applied to the currently-selected method column in the §9.7 what-if table.
  const selCol = { background: 'rgba(37,99,235,0.08)', fontWeight: 600 } as const;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 800,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              ลงทะเบียนค่าใช้จ่ายจ่ายล่วงหน้าใหม่
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              เลขที่ใหม่ <b style={{ color: '#3f3f46' }}>PPE-2026-000016</b> · จะลงบัญชี{' '}
              {PREPAID_ASSET_CODE} — ค่าใช้จ่ายจ่ายล่วงหน้า อัตโนมัติ
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div
          style={{
            padding: 22,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            overflow: 'auto',
          }}
        >
          {belowThreshold && (
            <div
              style={{
                background: 'var(--warning-bg, #fef3e8)',
                border: '1px solid var(--warning)',
                borderRadius: 8,
                padding: '10px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div style={{ flex: 1, fontSize: 14 }}>
                มูลค่า <b>{fmt(total)}</b> ต่ำกว่าเกณฑ์ความสำคัญ (<b>{fmt(threshold ?? 0)}</b>) —
                แนะนำให้ลงเป็นค่าใช้จ่ายงวดเดียวแทนการตัดจ่าย
                {expenseDirectErr && (
                  <span style={{ color: 'var(--negative)', marginLeft: 8 }}>
                    {expenseDirectErr}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="btn sm"
                disabled={!expenseAccountCode || expenseDirectMut.isPending}
                onClick={handleExpenseDirect}
              >
                {expenseDirectMut.isPending ? 'กำลังลง…' : 'ลงเป็นค่าใช้จ่ายงวดเดียว'}
              </button>
            </div>
          )}
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 8,
              }}
            >
              หมวดค่าใช้จ่าย
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
              {PP_CATS.map((c) => (
                <div
                  key={c.code}
                  onClick={() => setCat(c.code)}
                  style={{
                    border: cat === c.code ? '2px solid var(--brand)' : '1.5px solid var(--border)',
                    background: cat === c.code ? 'var(--brand-tint)' : 'white',
                    borderRadius: 8,
                    padding: '10px 10px',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: c.tone }} />
                    <span style={{ fontSize: 14, fontWeight: cat === c.code ? 600 : 500 }}>
                      {c.label}
                    </span>
                  </div>
                  <div className="meta" style={{ marginTop: 4, fontSize: 13 }}>
                    → {c.acct}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              บัญชีค่าใช้จ่าย (GL Code)
            </label>
            <select
              className="select"
              value={expenseAccountCode}
              onChange={(e) => setExpenseAccountCode(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            >
              <option value="">— เลือกบัญชีค่าใช้จ่าย —</option>
              {expenseAccounts.map((a) => (
                <option key={a.code} value={a.code}>
                  {a.code} — {a.nameTh}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                ผู้ขาย / คู่สัญญา
              </label>
              <input
                className="input"
                placeholder="เช่น บมจ. ทิพยประกันภัย"
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                อ้างอิงใบสำคัญจ่าย
              </label>
              <input
                className="input code"
                placeholder="PV-26050xxx"
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                สาขา
              </label>
              <BranchSelect
                companyId={companyId}
                value={branchId}
                onChange={setBranchId}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                หน่วยงาน
              </label>
              <DepartmentSelect
                companyId={companyId}
                value={departmentId}
                onChange={setDepartmentId}
                branchId={branchId}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>

          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              รายละเอียดสัญญา
            </label>
            <textarea
              className="input"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="เช่น ประกันภัยทรัพย์สินโรงงาน 1+2 ครอบคลุม 12 เดือน 1 มิ.ย. 2026 – 31 พ.ค. 2027"
              style={{
                width: '100%',
                padding: 10,
                fontSize: 14,
                resize: 'vertical',
                minHeight: 60,
              }}
            />
            {dup && (
              <div
                role="status"
                style={{
                  marginTop: 6,
                  fontSize: 12.5,
                  color: 'var(--warning,#b4780a)',
                  background: 'rgba(180,120,10,0.10)',
                  borderRadius: 6,
                  padding: '6px 10px',
                }}
              >
                ⚠ อาจซ้ำกับสัญญา {dup.id} ({dup.desc}) — ตรวจสอบก่อนบันทึก
              </div>
            )}
          </div>

          <div
            style={{
              padding: '14px 16px',
              background: '#fafafa',
              borderRadius: 8,
              border: '1px solid var(--border)',
            }}
          >
            <div className="section-title" style={{ margin: '0 0 10px' }}>
              การคำนวณตัดจ่าย
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 1.5fr',
                gap: 14,
                alignItems: 'flex-end',
              }}
            >
              <div>
                <label
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  มูลค่าสัญญา (บาท)
                </label>
                <input
                  className="input tnum"
                  type="number"
                  value={total}
                  onChange={(e) => setTotal(parseFloat(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    height: 38,
                    fontSize: 15,
                    textAlign: 'right',
                    fontWeight: 600,
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  หาร
                </label>
                <div
                  style={{
                    height: 38,
                    display: 'grid',
                    placeItems: 'center',
                    color: 'var(--text-faint)',
                    fontSize: 18,
                  }}
                >
                  ÷
                </div>
              </div>
              <div>
                <label
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  จำนวนงวด
                </label>
                <select
                  className="select"
                  value={period}
                  onChange={(e) => setPeriod(parseInt(e.target.value, 10))}
                  style={{ width: '100%', height: 38, fontSize: 14 }}
                >
                  <option value="3">3 เดือน</option>
                  <option value="6">6 เดือน</option>
                  <option value="12">12 เดือน (1 ปี)</option>
                  <option value="24">24 เดือน (2 ปี)</option>
                  <option value="36">36 เดือน (3 ปี)</option>
                  <option value="60">60 เดือน (5 ปี)</option>
                </select>
              </div>
              <div>
                <label
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  ตัดจ่ายต่อเดือน
                </label>
                <div
                  style={{
                    height: 38,
                    display: 'grid',
                    placeItems: 'center',
                    background: 'var(--brand)',
                    color: 'white',
                    borderRadius: 6,
                    fontSize: 16,
                    fontWeight: 700,
                    fontFamily: 'var(--font-display)',
                  }}
                  className="tnum"
                >
                  {fmt(monthly)}
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                วันที่จ่ายเงิน
              </label>
              <input
                className="input"
                type="date"
                defaultValue="2026-05-23"
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                เริ่มตัดจ่ายงวดแรก
              </label>
              <input
                className="input"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                ตัดจ่ายงวดสุดท้าย
              </label>
              <div
                style={{
                  height: 38,
                  display: 'grid',
                  placeItems: 'center',
                  background: 'white',
                  border: '1px solid var(--border-strong)',
                  borderRadius: 6,
                  fontSize: 14,
                  color: 'var(--text-muted)',
                }}
              >
                {endDate.toLocaleDateString('th-TH', {
                  day: '2-digit',
                  month: 'short',
                  year: '2-digit',
                  timeZone: 'UTC',
                })}
              </div>
            </div>
          </div>

          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <div className="section-title" style={{ margin: 0 }}>
                จำลองตารางตัดจ่าย — ทั้งหมด {period} งวด
              </div>
              {/* P9 §9.7 what-if — straight-line method toggle. Drives both the
                  highlighted column AND the schedule actually posted on submit. */}
              <div
                role="group"
                aria-label="วิธีตัดจ่าย"
                style={{
                  display: 'inline-flex',
                  border: '1px solid var(--border-strong)',
                  borderRadius: 6,
                  overflow: 'hidden',
                }}
              >
                <button
                  type="button"
                  className="btn sm ghost"
                  aria-pressed={method === 'EVEN'}
                  onClick={() => setMethod('EVEN')}
                  style={
                    method === 'EVEN'
                      ? {
                          background: 'var(--brand)',
                          color: 'white',
                          border: 'none',
                          borderRadius: 0,
                        }
                      : { border: 'none', borderRadius: 0 }
                  }
                >
                  เฉลี่ยเท่ากัน
                </button>
                <button
                  type="button"
                  className="btn sm ghost"
                  aria-pressed={method === 'PRORATE_FIRST_LAST'}
                  onClick={() => setMethod('PRORATE_FIRST_LAST')}
                  style={
                    method === 'PRORATE_FIRST_LAST'
                      ? {
                          background: 'var(--brand)',
                          color: 'white',
                          border: 'none',
                          borderRadius: 0,
                        }
                      : { border: 'none', borderRadius: 0 }
                  }
                >
                  เฉลี่ยตามจำนวนวัน
                </button>
              </div>
            </div>
            {whatIf.even.length === 0 ? (
              <div
                className="faint"
                style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 8 }}
              >
                กรอกจำนวนเงินและจำนวนงวดให้ถูกต้องเพื่อดูตารางจำลอง
              </div>
            ) : (
              <>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>
                  จะลงบัญชีด้วยวิธี{' '}
                  <b>
                    {method === 'EVEN'
                      ? 'เฉลี่ยเท่ากันทุกงวด'
                      : 'เฉลี่ยตามจำนวนวันจริง (งวดแรก/สุดท้ายตามสัดส่วนวัน)'}
                  </b>{' '}
                  — เดบิต {acctCode || '—'} {acctName} / เครดิต {PREPAID_ASSET_CODE}{' '}
                  ค่าใช้จ่ายจ่ายล่วงหน้า
                </div>
                <div
                  style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}
                >
                  <div style={{ maxHeight: 264, overflow: 'auto' }}>
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th style={{ width: 50 }}>งวด</th>
                          <th>เดือน</th>
                          <th className="num" style={method === 'EVEN' ? selCol : undefined}>
                            เฉลี่ยเท่ากัน
                          </th>
                          <th
                            className="num"
                            style={method === 'PRORATE_FIRST_LAST' ? selCol : undefined}
                          >
                            ตามจำนวนวัน
                          </th>
                          <th className="num">คงเหลือ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {whatIf.even.map((p, idx) => {
                          const pr = whatIf.prorate[idx];
                          const sel = previewSched[idx];
                          return (
                            <tr key={p.period}>
                              <td className="num">{p.index}</td>
                              <td>{thMonth(p)}</td>
                              <td
                                className="num tnum"
                                style={method === 'EVEN' ? selCol : undefined}
                              >
                                {fmt(p.amount.toNumber(), 2)}
                              </td>
                              <td
                                className="num tnum"
                                style={method === 'PRORATE_FIRST_LAST' ? selCol : undefined}
                              >
                                {fmt(pr ? pr.amount.toNumber() : 0, 2)}
                              </td>
                              <td className="num tnum faint">
                                {fmt(sel ? sel.closingBalance.toNumber() : 0, 2)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="total">
                          <td colSpan={2}>รวม {period} งวด</td>
                          <td className="num tnum" style={method === 'EVEN' ? selCol : undefined}>
                            {fmt(
                              whatIf.even.reduce((s, p) => s + p.amount.toNumber(), 0),
                              2,
                            )}
                          </td>
                          <td
                            className="num tnum"
                            style={method === 'PRORATE_FIRST_LAST' ? selCol : undefined}
                          >
                            {fmt(
                              whatIf.prorate.reduce((s, p) => s + p.amount.toNumber(), 0),
                              2,
                            )}
                          </td>
                          <td className="num tnum">0.00</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>

          <label
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            <input type="checkbox" defaultChecked />
            <span>เปิดใช้การตัดจ่ายอัตโนมัติทุกสิ้นเดือน — ระบบจะสร้าง JV ให้โดยไม่ต้องยืนยัน</span>
          </label>
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn"
              onClick={() => {
                /* TODO(phase-3): wire */
              }}
            >
              บันทึก & ดูตาราง
            </button>
            <button
              className="btn primary"
              onClick={handleSubmit}
              disabled={submitting || !expenseAccountCode}
            >
              <Icon name="check" size={14} />{' '}
              {submitting ? 'กำลังบันทึก…' : 'ลงทะเบียน & สร้างตาราง'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
